import {
  addDoc,
  deleteDoc,
  deleteField,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  attendeesCollectionRef,
  db,
  groupsCollectionRef,
} from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';
import {
  clearLabelUrlsForRefs,
  hasCachedLabel,
} from './label-cache';
import type { BulkClearLabelsResult } from './label-cache';
import type { Attendee, Group } from '../types';

const groupLabel = (g: { groupName: string }): string => g.groupName;

export const createGroup = async (
  actor: AuditActor | null,
  groupPayload: Record<string, unknown>,
): Promise<string> => {
  const docRef = await addDoc(groupsCollectionRef, groupPayload);
  const snapshot = stripSentinels(groupPayload);
  logActivity({
    actor,
    entity: 'group',
    entityRef: docRef.id,
    entityLabel: groupLabel(snapshot as { groupName: string }),
    action: 'create',
    eventRef: snapshot.eventRef as string | undefined,
    snapshot,
  });
  return docRef.id;
};

export const updateGroup = async (
  actor: AuditActor | null,
  before: Group,
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(groupsCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'group',
    entityRef: before.ref,
    entityLabel: groupLabel(before),
    action: 'update',
    eventRef: before.eventRef,
    changes,
  });
};

export const deleteGroup = async (
  actor: AuditActor | null,
  before: Group,
): Promise<void> => {
  await deleteDoc(doc(groupsCollectionRef, before.ref));
  const { ref, ...rest } = before;
  logActivity({
    actor,
    entity: 'group',
    entityRef: before.ref,
    entityLabel: groupLabel(before),
    action: 'delete',
    eventRef: before.eventRef,
    snapshot: rest as unknown as Record<string, unknown>,
  });
};

// Group fields that are rendered on the printed badge. When any of these
// change on a group, every attendee currently assigned to that group has a
// stale cached label and needs a fresh `s3LabelUrl`. Kept here (rather than
// at the caller) so the rule lives next to the write helper that enforces
// it. `rotationRef` is intentionally NOT in this list — rotations don't
// appear on the badge.
export const LABEL_RELEVANT_GROUP_FIELDS = [
  'grade',
  'groupName',
  'teachers',
] as const satisfies ReadonlyArray<keyof Group>;

// Clears cached `s3LabelUrl` on every attendee currently assigned to `group`
// who has one, then logs a single audit entry against the group. Skips the
// audit when nothing succeeded so the log only reflects real writes; meta
// carries `failed` when non-zero. Batching + per-batch error isolation live
// in `clearLabelUrlsForRefs`.
//
// `attendees` should be the in-memory list from `useFirebaseContext()` —
// callers pre-supply it rather than the helper re-fetching, matching the
// `softDeleteAttendee(checkInDates=[])` pattern and avoiding a redundant
// query that would race against the same snapshot the UI is already showing.
export const clearLabelUrlsForGroup = async (
  actor: AuditActor | null,
  group: Group,
  attendees: Attendee[],
): Promise<BulkClearLabelsResult> => {
  const refs = attendees
    .filter((a) => a.groupRef === group.ref && hasCachedLabel(a.s3LabelUrl))
    .map((a) => a.ref);

  const result = await clearLabelUrlsForRefs(refs, 'clearLabelUrlsForGroup');
  if (result.cleared > 0) {
    logActivity({
      actor,
      entity: 'group',
      entityRef: group.ref,
      entityLabel: groupLabel(group),
      action: 'clear-labels',
      eventRef: group.eventRef,
      meta:
        result.failed > 0
          ? { count: result.cleared, failed: result.failed }
          : { count: result.cleared },
    });
  }
  return result;
};

// Deletes a group after moving attendees to `targetGroupRef` in one batch.
// The reassignment + delete must be atomic to avoid an orphan window where
// the group is gone but its attendees still point at it. Each attendee's
// `s3LabelUrl` is cleared in the same update because the printed badge
// reflects the (now-changing) group's name/grade/teachers; without this,
// the next print would still show the deleted group's data until the
// attendee is edited again or the admin bulk-clears.
//
// v2 (2026-05-31): `group_preference_fulfilled` is NOT cleared here.
// Status / note are admin assertions about the preference, not the
// placement; admin reconciles if a reassignment made them stale.
export const deleteGroupWithReassignment = async (
  actor: AuditActor | null,
  group: Group,
  targetGroupRef: string,
  attendeeRefs: string[],
): Promise<void> => {
  const batch = writeBatch(db);
  attendeeRefs.forEach((attendeeRef) => {
    batch.update(doc(attendeesCollectionRef, attendeeRef), {
      groupRef: targetGroupRef,
      s3LabelUrl: deleteField(),
    });
  });
  batch.delete(doc(groupsCollectionRef, group.ref));
  await batch.commit();
  const { ref, ...rest } = group;
  logActivity({
    actor,
    entity: 'group',
    entityRef: group.ref,
    entityLabel: groupLabel(group),
    action: 'delete',
    eventRef: group.eventRef,
    snapshot: rest as unknown as Record<string, unknown>,
    meta: {
      reassignedAttendeeCount: attendeeRefs.length,
      targetGroupRef,
    },
  });
};
